// Bar aggregation, kept pure and DOM-free so it can be tested directly.

import type { Bar, Trade } from './engine';

/** Minute (or hourly) bars: backfilled candles, extended by prints after `printsFrom` (the backfill instant). */
export function buildMinuteBars(
  backfill: readonly Bar[], prints: readonly Trade[], printsFrom: number,
  spanMs: number, now: number,
): Bar[] {
  const byBucket = new Map<number, Bar>();
  for (const b of backfill) {
    const k = Math.floor((b.t1 - 1) / spanMs) * spanMs;
    byBucket.set(k, { ...b, t0: k, t1: k + spanMs });
  }
  for (const tr of prints) {
    if (tr.t < printsFrom) continue;
    const k = Math.floor(tr.t / spanMs) * spanMs;
    const cur = byBucket.get(k);
    if (!cur) {
      byBucket.set(k, {
        t0: k, t1: k + spanMs, o: tr.priceMc, h: tr.priceMc, l: tr.priceMc,
        c: tr.priceMc, vol: tr.mq, n: 1,
      });
    } else {
      cur.h = Math.max(cur.h, tr.priceMc);
      cur.l = Math.min(cur.l, tr.priceMc);
      cur.c = tr.priceMc;
      cur.vol += tr.mq;
      cur.n += 1;
    }
  }
  const out = [...byBucket.values()].sort((a, b) => a.t0 - b.t0);
  const nowBucket = Math.floor(now / spanMs) * spanMs;
  for (const b of out) if (b.t0 === nowBucket) b.forming = true;
  return out;
}

/** One bar per `n` prints, oldest first. The trailing partial bar is forming. */
export function buildTickBars(prints: readonly Trade[], n: number): Bar[] {
  const size = Math.max(1, Math.floor(n));
  const out: Bar[] = [];
  let cur: Bar | null = null;
  for (const tr of prints) {
    if (!cur) {
      cur = {
        t0: tr.t, t1: tr.t, o: tr.priceMc, h: tr.priceMc, l: tr.priceMc,
        c: tr.priceMc, vol: tr.mq, n: 1,
      };
    } else {
      cur.h = Math.max(cur.h, tr.priceMc);
      cur.l = Math.min(cur.l, tr.priceMc);
      cur.c = tr.priceMc;
      cur.vol += tr.mq;
      cur.n += 1;
      cur.t1 = tr.t;
    }
    if (cur.n >= size) { out.push(cur); cur = null; }
  }
  if (cur) { cur.forming = true; out.push(cur); }
  return out;
}
